import type { GridColDef } from '@mui/x-data-grid';

import { PaySchedule } from '@vyce/core/src/types';

/**
 * Payroll visibility takes two questions, not one: does the person hold the right,
 * and is the operation possible on this schedule at all. Vyce runs payroll on an
 * outsourced schedule, so payments, BACS and payslips are not the company's to
 * perform there — the server answers 409 for them regardless of who asks, the
 * owner included. Pay periods are NOT in that set: the company creates its own
 * timesheets whoever runs the payroll.
 */
export const isOutsourcedSchedule = (schedule?: { outsourced?: boolean } | null): boolean =>
  !!schedule?.outsourced;

interface PayrollMenuFlags {
  /** interface:can_access_payroll — the company has at least one in-house schedule. */
  canAccessPayroll: boolean;
  /** interface:can_access_payroll_outsourced. */
  canAccessPayrollOutsourced: boolean;
  /**
   * Owners answer `true` to every permission check, so their menu flags say nothing
   * about the company and only the schedules do. And the rule still applies to them:
   * the owner of an outsourced company gets no payments, BACS, HMRC or bank details.
   */
  isOwner?: boolean;
}

/**
 * Whether the company has no in-house schedule at all. The company-level operations
 * — HMRC settings, company bank details, CIS300 — don't exist for such a company.
 *
 * Two sources, in this order: the menu flags, which arrive with the user and are
 * therefore available before anything is fetched, then the schedules list. A mixed
 * company (one in-house schedule, one outsourced) is normal and is NOT this case —
 * there the per-schedule check decides, row by row.
 */
export const isOutsourcedOnlyCompany = (
  paySchedules: PaySchedule[] | undefined,
  { canAccessPayroll, canAccessPayrollOutsourced, isOwner }: PayrollMenuFlags
): boolean => {
  if (!isOwner) {
    if (canAccessPayroll) {
      return false;
    }
    if (canAccessPayrollOutsourced) {
      return true;
    }
  }
  // Nothing to read off the flags: fall back to the schedules, and only once they
  // have loaded — an empty list is "not known yet", not "outsourced".
  return !!paySchedules?.length && paySchedules.every(schedule => isOutsourcedSchedule(schedule));
};

/**
 * Puts the backend's calculation note for each column into the grid header tooltip.
 * `lines` is keyed by the data field a column shows; `dataKeys` maps a grid field to that
 * data field where they differ. A column without a note gets no tooltip.
 */
export const applyColumnHovers = (
  columns: GridColDef[],
  lines?: Record<string, string>,
  dataKeys: Record<string, string> = {}
): GridColDef[] =>
  columns.map(column => {
    const description = lines?.[dataKeys[column.field] ?? column.field];
    return description ? { ...column, description } : column;
  });
